import { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import DictationInput from '../../components/DictationInput';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { Badge } from '../../components/ui/badge';
import { Checkbox } from '../../components/ui/checkbox';
import { toast } from 'sonner';
import { OurSquareLogo } from '../../components/brand/Logo';
import { 
  FileText, Download, Eye, Calendar, User, Mail, 
  CheckCircle, AlertTriangle, Clock, Shield, Activity,
  ChevronRight, Send
} from 'lucide-react';

export default function ComplianceReportsPage() {
  const { axiosAuth } = useAuth();
  const api = axiosAuth();
  const [residents, setResidents] = useState([]);
  const [reportHistory, setReportHistory] = useState([]);
  const [selectedResident, setSelectedResident] = useState(null);
  const [previewData, setPreviewData] = useState(null);
  const [generateOpen, setGenerateOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  
  const today = new Date().toISOString().split('T')[0];
  const thirtyDaysAgo = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
  
  const [reportForm, setReportForm] = useState({
    resident_id: '',
    period_start: thirtyDaysAgo,
    period_end: today,
    include_incidents: true,
    include_milestones: true,
    include_attendance: true,
    include_drug_tests: true,
    staff_notes: '',
    recipient_name: '',
    recipient_email: '',
    recipient_title: 'Probation Officer',
  });

  useEffect(() => { load(); }, []);

  const load = async () => {
    setLoading(true);
    try {
      const [housesRes, historyRes] = await Promise.all([
        api.get('/housing/entity/our_square/houses'),
        api.get('/compliance-reports/history?entity=our_square'),
      ]);
      const allResidents = housesRes.data.flatMap(h => 
        (h.residents || []).map(r => ({ ...r, house_name: h.name }))
      );
      setResidents(allResidents);
      setReportHistory(historyRes.data);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const loadPreview = async () => {
    if (!reportForm.resident_id) return toast.error('Select a resident');
    try {
      const res = await api.get(`/compliance-reports/preview/${reportForm.resident_id}?period_start=${reportForm.period_start}&period_end=${reportForm.period_end}`);
      setPreviewData(res.data);
      setSelectedResident(residents.find(r => r.id === reportForm.resident_id));
    } catch (e) { toast.error('Failed to load preview'); }
  };

  const generatePDF = async () => {
    if (!reportForm.resident_id) return toast.error('Select a resident');
    try {
      const response = await api.post('/compliance-reports/generate', reportForm, {
        responseType: 'blob',
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const resident = residents.find(r => r.id === reportForm.resident_id);
      link.setAttribute('download', `compliance_report_${resident?.user_name?.replace(/\s/g, '_')}_${reportForm.period_start}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      // Save report record
      await api.post('/compliance-reports/save', reportForm);
      
      toast.success('Report generated and downloaded!');
      setGenerateOpen(false);
      load();
    } catch (e) { toast.error('Failed to generate report'); }
  };

  const getScoreColor = (score) => {
    if (score <= 3) return 'text-red-600';
    if (score <= 6) return 'text-amber-600';
    return 'text-emerald-600';
  };

  return (
    <div className="space-y-6" data-testid="compliance-reports-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <OurSquareLogo size="sm" className="h-12 w-auto" />
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Compliance Reports</h1>
            <p className="text-sm text-muted-foreground">Generate reports for probation officers and case managers</p>
          </div>
        </div>
        <Dialog open={generateOpen} onOpenChange={setGenerateOpen}>
          <DialogTrigger asChild>
            <Button className="bg-cyan-600 hover:bg-cyan-700 text-white rounded-full gap-2">
              <FileText size={16} /> Generate Report
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto rounded-2xl">
            <DialogHeader><DialogTitle>Generate Compliance Report</DialogTitle></DialogHeader>
            <div className="space-y-4 pt-2">
              {/* Resident Selection */}
              <div>
                <Label>Select Resident *</Label>
                <Select value={reportForm.resident_id} onValueChange={v => setReportForm({ ...reportForm, resident_id: v })}>
                  <SelectTrigger><SelectValue placeholder="Choose resident" /></SelectTrigger>
                  <SelectContent>
                    {residents.map(r => (
                      <SelectItem key={r.id} value={r.id}>{r.user_name} ({r.house_name})</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Date Range */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Period Start</Label>
                  <Input type="date" value={reportForm.period_start} onChange={e => setReportForm({ ...reportForm, period_start: e.target.value })} />
                </div>
                <div>
                  <Label>Period End</Label>
                  <Input type="date" value={reportForm.period_end} onChange={e => setReportForm({ ...reportForm, period_end: e.target.value })} />
                </div>
              </div>

              {/* Recipient Info */}
              <div className="p-4 bg-slate-50 rounded-xl space-y-3">
                <p className="text-sm font-medium text-slate-700">Report Recipient (Optional)</p>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">Name</Label>
                    <Input value={reportForm.recipient_name} onChange={e => setReportForm({ ...reportForm, recipient_name: e.target.value })} placeholder="e.g., John Smith" />
                  </div>
                  <div>
                    <Label className="text-xs">Title</Label>
                    <Select value={reportForm.recipient_title} onValueChange={v => setReportForm({ ...reportForm, recipient_title: v })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Probation Officer">Probation Officer</SelectItem>
                        <SelectItem value="Parole Officer">Parole Officer</SelectItem>
                        <SelectItem value="Case Manager">Case Manager</SelectItem>
                        <SelectItem value="Social Worker">Social Worker</SelectItem>
                        <SelectItem value="Therapist">Therapist</SelectItem>
                        <SelectItem value="Other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <Label className="text-xs">Email</Label>
                  <Input type="email" value={reportForm.recipient_email} onChange={e => setReportForm({ ...reportForm, recipient_email: e.target.value })} placeholder="For record keeping" />
                </div>
              </div>

              {/* Include Options */}
              <div className="p-4 bg-slate-50 rounded-xl space-y-3">
                <p className="text-sm font-medium text-slate-700">Include in Report</p>
                <div className="grid grid-cols-2 gap-3">
                  <label className="flex items-center gap-2 text-sm">
                    <Checkbox checked={reportForm.include_incidents} onCheckedChange={v => setReportForm({ ...reportForm, include_incidents: v })} />
                    Incident Details
                  </label>
                  <label className="flex items-center gap-2 text-sm">
                    <Checkbox checked={reportForm.include_milestones} onCheckedChange={v => setReportForm({ ...reportForm, include_milestones: v })} />
                    Program Milestones
                  </label>
                  <label className="flex items-center gap-2 text-sm">
                    <Checkbox checked={reportForm.include_attendance} onCheckedChange={v => setReportForm({ ...reportForm, include_attendance: v })} />
                    Meeting Attendance
                  </label>
                  <label className="flex items-center gap-2 text-sm">
                    <Checkbox checked={reportForm.include_drug_tests} onCheckedChange={v => setReportForm({ ...reportForm, include_drug_tests: v })} />
                    Drug Test Results
                  </label>
                </div>
              </div>

              {/* Staff Notes */}
              <div>
                <Label>Staff Notes (will appear in report)</Label>
                <DictationInput value={reportForm.staff_notes} onChange={e => setReportForm({ ...reportForm, staff_notes: e.target.value })} rows={3} placeholder="Add any additional observations or context..." showCleanup={false} />
              </div>

              {/* Actions */}
              <div className="flex gap-3 pt-2">
                <Button variant="outline" onClick={loadPreview} className="flex-1 rounded-full gap-2">
                  <Eye size={14} /> Preview Data
                </Button>
                <Button onClick={generatePDF} className="flex-1 bg-cyan-600 hover:bg-cyan-700 text-white rounded-full gap-2">
                  <Download size={14} /> Generate PDF
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Quick Generate Cards */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {residents.slice(0, 6).map(r => (
          <Card key={r.id} className="border-0 shadow-sm rounded-xl hover:shadow-md transition-shadow cursor-pointer" onClick={() => { setReportForm({ ...reportForm, resident_id: r.id }); setGenerateOpen(true); }}>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-cyan-100 flex items-center justify-center text-cyan-700 font-bold">
                  {r.user_name?.charAt(0)}
                </div>
                <div className="flex-1">
                  <p className="font-medium text-sm">{r.user_name}</p>
                  <p className="text-xs text-muted-foreground">{r.house_name}</p>
                </div>
                <FileText size={18} className="text-cyan-600" />
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Preview Data */}
      {previewData && selectedResident && (
        <Card className="border-0 shadow-sm rounded-xl">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center justify-between">
              <span>Report Preview: {selectedResident.user_name}</span>
              <Badge variant="outline">{previewData.period_start} to {previewData.period_end}</Badge>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {/* Risk Score */}
              <div className="p-4 bg-slate-50 rounded-xl">
                <div className="flex items-center gap-2 mb-2">
                  <Shield size={16} className="text-slate-500" />
                  <span className="text-xs text-muted-foreground">Stability Score</span>
                </div>
                <p className={`text-2xl font-bold ${getScoreColor(previewData.data.risk_score?.score)}`}>
                  {previewData.data.risk_score?.score}/10
                </p>
                <p className="text-xs">{previewData.data.risk_score?.level}</p>
              </div>

              {/* Curfew */}
              <div className="p-4 bg-slate-50 rounded-xl">
                <div className="flex items-center gap-2 mb-2">
                  <Clock size={16} className="text-slate-500" />
                  <span className="text-xs text-muted-foreground">Curfew Compliance</span>
                </div>
                <p className="text-2xl font-bold">{previewData.data.curfew?.compliance_rate}%</p>
                <p className="text-xs">{previewData.data.curfew?.violations} violations</p>
              </div>

              {/* Drug Tests */}
              <div className="p-4 bg-slate-50 rounded-xl">
                <div className="flex items-center gap-2 mb-2">
                  <Activity size={16} className="text-slate-500" />
                  <span className="text-xs text-muted-foreground">Drug Tests</span>
                </div>
                <p className="text-2xl font-bold">{previewData.data.drug_tests?.pass_rate}%</p>
                <p className="text-xs">{previewData.data.drug_tests?.passed}/{previewData.data.drug_tests?.total} passed</p>
              </div>

              {/* Incidents */}
              <div className="p-4 bg-slate-50 rounded-xl">
                <div className="flex items-center gap-2 mb-2">
                  <AlertTriangle size={16} className="text-slate-500" />
                  <span className="text-xs text-muted-foreground">Incidents</span>
                </div>
                <p className="text-2xl font-bold">{previewData.data.incidents?.count}</p>
                <p className="text-xs">in period</p>
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-4 mt-4">
              {/* Milestones */}
              <div className="p-4 bg-emerald-50 rounded-xl">
                <div className="flex items-center gap-2 mb-2">
                  <CheckCircle size={16} className="text-emerald-600" />
                  <span className="text-sm font-medium text-emerald-700">Program Milestones</span>
                </div>
                <p className="text-lg font-bold text-emerald-700">
                  {previewData.data.milestones?.completed}/{previewData.data.milestones?.total} completed
                </p>
              </div>

              {/* Meetings */}
              <div className="p-4 bg-cyan-50 rounded-xl">
                <div className="flex items-center gap-2 mb-2">
                  <User size={16} className="text-cyan-600" />
                  <span className="text-sm font-medium text-cyan-700">Meeting Attendance</span>
                </div>
                <p className="text-lg font-bold text-cyan-700">
                  {previewData.data.meetings?.attendance_rate}% ({previewData.data.meetings?.attended}/{previewData.data.meetings?.total_scheduled})
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Report History */}
      <Card className="border-0 shadow-sm rounded-xl">
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Report History</CardTitle>
        </CardHeader>
        <CardContent>
          {reportHistory.length > 0 ? (
            <div className="space-y-2">
              {reportHistory.map(report => (
                <div key={report.id} className="flex items-center justify-between p-4 rounded-lg bg-slate-50">
                  <div className="flex items-center gap-3">
                    <FileText size={20} className="text-cyan-600" />
                    <div>
                      <p className="font-medium text-sm">{report.resident_name}</p>
                      <p className="text-xs text-muted-foreground">
                        {report.period_start} to {report.period_end}
                        {report.recipient_name && ` • Sent to ${report.recipient_name}`}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-muted-foreground">{new Date(report.created_at).toLocaleDateString()}</p>
                    <p className="text-xs text-slate-500">by {report.generated_by_name}</p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-muted-foreground">
              <FileText className="w-10 h-10 mx-auto mb-3 text-slate-300" />
              <p className="text-sm">No reports generated yet</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
